/**
 * <kc-fabric-detail> — "Stoff im Detail": large, zoomable fabric photos with
 * the fabric's description and specs (snippets/kc-fabric-detail.liquid).
 *
 * Opened by any [data-fabric-detail-open][aria-controls="<id>"]: on the colour
 * currently selected, or — from inside the colour sheet (data-from-sheet) — on
 * the colour the sheet is showing. It shows that colour's fabric panel and
 * starts the photo track on that colour.
 *
 * "Diese Farbe wählen" goes through the colour sheet (selectValue), so the
 * gallery photo swaps at once and the variant changes exactly as a sheet tile
 * would. Like the sheet, this element is preserved across the section
 * re-render that follows; a clone carries no listeners or scroll position,
 * hence the delegated openers and the re-seek in connectedCallback.
 */

const MOBILE = window.matchMedia("(max-width: 768px)");

class KcFabricDetail extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.addEventListener("click", (event) => this.#onClick(event));
    this.addEventListener("pointermove", (event) => this.#onPointerMove(event));
    this.querySelectorAll("[data-detail-track]").forEach((track) => {
      track.addEventListener("scroll", () => this.#onScroll(track), { passive: true });
    });

    if (this.isOpen) {
      // Re-render clone: no entrance animation, back to the colour on show.
      this.setAttribute("data-settled", "");
      requestAnimationFrame(() => this.#goTo(this.dataset.value, false));
    }
  }

  get isOpen() {
    return this.classList.contains("is-open");
  }

  get #sheet() {
    return document.querySelector(`kc-fabric-sheet[data-form-id="${CSS.escape(this.dataset.formId || "")}"]`);
  }

  get #panel() {
    return this.querySelector(".kc-fabric-detail__panel:not([hidden])");
  }

  get #track() {
    return this.#panel?.querySelector("[data-detail-track]") || null;
  }

  #selectedValue() {
    const form = document.getElementById(this.dataset.formId);
    const radio = form?.querySelector(`input[type="radio"][name="${CSS.escape(this.dataset.optionName || "")}"]:checked`);
    return radio ? radio.value : null;
  }

  open(opener) {
    let value = null;
    if (opener && opener.hasAttribute("data-from-sheet")) value = this.#sheet?.detailValue || null;
    if (!value) value = this.#selectedValue();

    const slide = value ? this.#slideFor(value) : null;
    const panel = slide ? slide.closest(".kc-fabric-detail__panel") : this.querySelector(".kc-fabric-detail__panel");
    if (!panel) return;

    this.querySelectorAll(".kc-fabric-detail__panel").forEach((p) => {
      p.hidden = p !== panel;
    });

    this._opener = opener || null;
    this.removeAttribute("data-settled");
    this.#portal();
    this.classList.add("is-open");
    if (!MOBILE.matches || !document.querySelector("kc-fabric-sheet.is-open")) {
      document.documentElement.classList.add("lock");
    }

    this.#goTo(value || panel.querySelector(".kc-fabric-detail__slide")?.dataset.value, false);
    this.querySelector(".kc-fabric-detail__dialog").scrollTop = 0;
    this.querySelector(".kc-fabric-detail__close")?.focus({ preventScroll: true });
  }

  close() {
    if (!this.isOpen) return;
    this.#unzoomAll();
    this.classList.remove("is-open");
    this.removeAttribute("data-settled");
    document.documentElement.classList.remove("lock");
    this.#unportal();
    const opener = this._opener && this._opener.isConnected
      ? this._opener
      : document.querySelector(`[data-fabric-detail-open][aria-controls="${this.id}"]`);
    opener?.focus({ preventScroll: true });
  }

  // While open the dialog lives at the end of <body>: inside the product
  // section it sits in that section's stacking context, under the site header.
  // A placeholder keeps its place, and closing puts it back there — before the
  // variant re-render a colour choice triggers — so the re-render still finds
  // (and preserves) it inside the section.
  #portal() {
    if (this.parentElement === document.body) return;
    this._placeholder = document.createComment("kc-fabric-detail");
    this.replaceWith(this._placeholder);
    document.body.appendChild(this);
  }

  #unportal() {
    const placeholder = this._placeholder;
    this._placeholder = null;
    if (placeholder && placeholder.isConnected) placeholder.replaceWith(this);
  }

  #slideFor(value) {
    return Array.from(this.querySelectorAll(".kc-fabric-detail__slide")).find((s) => s.dataset.value === value) || null;
  }

  #goTo(value, smooth) {
    const track = this.#track;
    if (!track || !value) return;
    const slide = Array.from(track.children).find((s) => s.dataset.value === value);
    if (!slide) return;
    this.#unzoomAll();
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: smooth ? "smooth" : "instant" });
    this.#setCurrent(value);
  }

  #onScroll(track) {
    if (track.closest(".kc-fabric-detail__panel")?.hidden) return;
    const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    const slide = track.children[index];
    if (slide && slide.dataset.value !== this.dataset.value) this.#setCurrent(slide.dataset.value);
  }

  #setCurrent(value) {
    this.dataset.value = value;
    const panel = this.#panel;
    if (!panel) return;

    const slides = Array.from(panel.querySelectorAll(".kc-fabric-detail__slide"));
    const index = slides.findIndex((s) => s.dataset.value === value);
    panel.querySelector('[data-detail-step="-1"]')?.toggleAttribute("disabled", index <= 0);
    panel.querySelector('[data-detail-step="1"]')?.toggleAttribute("disabled", index >= slides.length - 1);

    panel.querySelectorAll("[data-detail-goto]").forEach((chip) => {
      chip.setAttribute("aria-current", chip.dataset.detailGoto === value ? "true" : "false");
    });

    const choose = panel.querySelector("[data-detail-choose]");
    if (choose) {
      const selected = value === this.#selectedValue();
      const name = slides[index]?.dataset.name;
      choose.setAttribute("aria-disabled", selected ? "true" : "false");
      choose.textContent = selected
        ? `${this.dataset.selectedLabel}${name ? ` · ${name}` : ""}`
        : `${this.dataset.chooseLabel}${name ? ` · ${name}` : ""}`;
    }
  }

  #onClick(event) {
    if (event.target.closest("[data-detail-close]")) {
      this.close();
      return;
    }

    const step = event.target.closest("[data-detail-step]");
    if (step) {
      const slides = Array.from(this.#panel?.querySelectorAll(".kc-fabric-detail__slide") || []);
      const index = slides.findIndex((s) => s.dataset.value === this.dataset.value);
      const next = slides[index + Number(step.dataset.detailStep)];
      if (next) this.#goTo(next.dataset.value, true);
      return;
    }

    const chip = event.target.closest("[data-detail-goto]");
    if (chip) {
      this.#goTo(chip.dataset.detailGoto, true);
      return;
    }

    const choose = event.target.closest("[data-detail-choose]");
    if (choose) {
      if (choose.getAttribute("aria-disabled") === "true") return;
      const value = this.dataset.value;
      const sheet = this.#sheet;
      this.close();
      if (!sheet || !value) return;
      sheet.selectValue(value);
      // On a phone with the sheet closed, show the result on the sofa.
      if (MOBILE.matches && !sheet.isOpen) {
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        (sheet.closest(".shopify-section") || document)
          .querySelector(".product-gallery__ar-wrapper, .product-gallery-wrapper")
          ?.scrollIntoView({ block: "start", behavior: reduced ? "instant" : "smooth" });
      }
      return;
    }

    const zoom = event.target.closest("[data-detail-zoom]");
    if (zoom) this.#toggleZoom(zoom, event);
  }

  // Zoom: click/tap toggles a 2.5x close-up centred where the customer
  // pointed; while zoomed, moving the pointer (or dragging a finger) pans.
  #toggleZoom(zoom, event) {
    const zoomed = !zoom.classList.contains("is-zoomed");
    this.#unzoomAll();
    if (!zoomed) return;
    this.#setOrigin(zoom, event);
    zoom.classList.add("is-zoomed");
    zoom.closest("[data-detail-track]")?.classList.add("is-zoomed");
  }

  #onPointerMove(event) {
    const zoom = event.target.closest("[data-detail-zoom].is-zoomed");
    if (zoom) this.#setOrigin(zoom, event);
  }

  #setOrigin(zoom, event) {
    const rect = zoom.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
    zoom.style.setProperty("--kc-zoom-x", `${x}%`);
    zoom.style.setProperty("--kc-zoom-y", `${y}%`);
  }

  #unzoomAll() {
    this.querySelectorAll(".is-zoomed").forEach((el) => el.classList.remove("is-zoomed"));
  }
}

if (!customElements.get("kc-fabric-detail")) {
  customElements.define("kc-fabric-detail", KcFabricDetail);

  document.addEventListener("click", (event) => {
    const opener = event.target.closest("[data-fabric-detail-open]");
    if (!opener) return;
    const detail = document.getElementById(opener.getAttribute("aria-controls"));
    if (detail && typeof detail.open === "function") {
      event.preventDefault();
      detail.open(opener);
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    const open = document.querySelector("kc-fabric-detail.is-open");
    if (open) {
      event.stopImmediatePropagation();
      open.close();
    }
  }, true);
}
